import { patchHistoryStateForBarba } from './core/utils/history-proxy.js';
import { initializeScrollManager, destroyScrollManager, initializeLenisAnchorScrolling, handleHashScroll } from './core/scroll-manager.js';
import { initializeScrollProgressIndicators, destroyScrollProgressIndicators, initScrollPercentElements, destroyScrollPercentElements, initScrollVarElements, destroyScrollVarElements } from './core/scroll-utils.js';
import { initializeScrollEffects, destroyScrollEffects } from './core/scroll-effects.js';
import { initializeParallax, destroyParallax } from './core/parallax.js';
import { initializeVideoCoverResizerListeners, resizeIframesToCover } from './core/video-cover-resizer.js';
import { querystringDragging } from './core/utils/url-utils.js';
import { initializeDomLoadListeners } from './core/utils/dom-load-listeners.js';
import { loadWebFonts } from './core/web-fonts.js';
import { updateNavMenuClasses } from './core/nav-menu-updater.js';
import { initializeMobileMenu } from './ui/header/mobile-menu.js';
import { initializeStickyHeader, destroyStickyHeader } from './ui/header/sticky.js';
import { initializeBarbaTransitions } from './integrations/barba.js';
import { initializeAlmFeatures } from './integrations/alm/alm-features.js';
import { initializeObfuscatedLinks } from './core/utils/obfuscate-links.js';
import { initializeThirdPartyEmbeds } from './integrations/third-party-embeds.js';
import { initAlmMasonry, destroyAlmMasonry } from './integrations/alm/alm-masonry.js';
import { monitorMediaLoading } from './core/page-loader.js';
import { Logger } from './core/utils/logger.js';
import { setBreakpoints } from './core/utils/breakpoints.js';

// --- Patch History API early to prevent Barba state overwrites ---
patchHistoryStateForBarba();

// =============================================================================
// THEME CONFIGURATION
// Centralized settings, timings, feature toggles, and font definitions.
// =============================================================================

const THEME_CONFIG = {
    // Site Breakpoints (max-width thresholds in px — adjust per site)
    breakpoints: {
        xs: 640,
        md: 1080,
        lg: 1920,
    },

    // Debug logging enabled everywhere except production
    debug: (window.app?.environment) 
        ? window.app.environment !== 'production' 
        : process.env.WP_ENVIRONMENT_TYPE !== 'production',

    // Transition Choreography Timings (ms)
    timings: {
        leaveMinDuration: 500,   // Min loader display time during leave()
        leaveProgressNudge: 100, // Delay before progress bar activity nudge
        loaderFadeOut: 500,      // Loader fade-out animation duration
    },

    // Feature Flags (Toggle any theme component on/off globally)
    features: {
        accordions: true,         // Accordions (.accordion)
        tabs: true,               // Tabs (.tabs)
        fauxSelects: true,        // Custom styled selects
        readReveal: true,         // Expandable read-more text blocks
        quickSearch: true,        // Instant search dropdowns
        imageZoom: true,          // Lightbox / Image zoom dialogs
        faqs: true,               // FAQ filter archive & pagination
        infoBubbles: false,       // Inline tooltip bubbles
        videoPlayers: true,       // Custom HTML5 / Vimeo / YouTube video players
        propertiesMap: false,     // Interactive Google Maps property listings
        countUp: true,            // Animated stat card number counters
        heroTaglines: true,       // Animated rotating tagline hero headers
        heroScrollOpacity: true,  // Scroll-driven hero overlay opacity
        scrollToTop: true,        // Floating "Scroll to Top" button
        slideshows: true,         // Swiper slideshows & WP galleries
        googleMaps: true,         // Embedded Google Maps
    },

    // Query String Decoration Configuration
    querystringDragging: {
        domains: [window.location.hostname],
        params: ['utm_medium', 'utm_source', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'fbclid']
    }
};

// Initialize central breakpoint manager
setBreakpoints(THEME_CONFIG.breakpoints);

const logger = new Logger(THEME_CONFIG.debug);

// =============================================================================
// COMPONENT REGISTRY & LIFECYCLE MANAGEMENT
// Single source of truth for per-page component initialization and teardown.
// =============================================================================

let lenisInstance;
let pageAbortController = null;
const activeModules = new Set();

const MODULES = [
    // --- Core Framework Infrastructure (Always Active) ---
    { name: 'nav-menu', init: ({ data }) => {
        if (data?.next?.url?.href) {
            updateNavMenuClasses(data.next.url.href, ['.header__primary']);
        }
    } },
    { name: 'scroll-progress', init: ({ lenis }) => { if (lenis) initializeScrollProgressIndicators('[data-js-scroll-distance="1"]', lenis); }, destroy: () => destroyScrollProgressIndicators() },
    { name: 'scroll-percent',  init: ({ lenis }) => { if (lenis) initScrollPercentElements(lenis); }, destroy: () => destroyScrollPercentElements() },
    { name: 'scroll-var',      init: ({ lenis }) => { if (lenis) initScrollVarElements(lenis); }, destroy: () => destroyScrollVarElements() },
    { name: 'hash-scroll',     init: ({ lenis, data }) => {
        // Link-click Barba transitions are scrolled accurately in afterEnter (after
        // media has loaded and lenis has resized), so skip them here to avoid a
        // premature scroll to a pre-reflow position. This still covers initial page
        // load and non-link navigations.
        if (data && data.trigger && typeof data.trigger === 'object') return;
        if (lenis) handleHashScroll(lenis, data?.next?.url?.hash ?? null);
    } },
    { name: 'sticky-header',   hasElement: '#header', init: ({ lenis }) => { if (lenis) initializeStickyHeader(lenis); }, destroy: () => destroyStickyHeader() },
    { name: 'scroll-effects',  init: ({ container }) => initializeScrollEffects(container), destroy: () => destroyScrollEffects() },
    { name: 'scroll-spy',      hasElement: 'a[href*="#"]', init: async () => {
        const { initializeScrollSpy } = await import('./ui/scroll-spy.js');
        initializeScrollSpy();
    }, destroy: async () => {
        const { destroyScrollSpy } = await import('./ui/scroll-spy.js');
        destroyScrollSpy();
    } },
    { name: 'parallax',        hasElement: '[data-parallax]', init: ({ container }) => initializeParallax(container), destroy: () => destroyParallax() },
    { name: 'modals',          hasElement: '[data-micromodal-trigger], .modal', init: async ({ debug }) => {
        const { initializeModals } = await import('./ui/modal-manager.js');
        initializeModals(debug);
    }, destroy: async () => {
        const { destroyModals } = await import('./ui/modal-manager.js');
        destroyModals();
    } },
    { name: 'team-modal',      container: true, hasElement: '.team-listing, #team-modal', init: async () => {
        const { initializeTeamModal } = await import('./ui/team-modal.js');
        initializeTeamModal();
    }, destroy: async () => {
        const { destroyTeamModal } = await import('./ui/team-modal.js');
        destroyTeamModal();
    } },
    { name: 'querystring-dragging', init: ({ debug }) => querystringDragging(THEME_CONFIG.querystringDragging.domains, THEME_CONFIG.querystringDragging.params, debug) },
    { name: 'video-cover',     hasElement: '.video-container', init: ({ container }) => resizeIframesToCover('.video-container', container) },
    { name: 'obfuscated-links', container: true, hasElement: '[data-email], [data-phone]', init: ({ container }) => initializeObfuscatedLinks(container) },
    { name: 'third-party-embeds', container: true, enabled: () => (window.app?.settings?.thirdPartyEmbeds?.length > 0), init: ({ container }) => initializeThirdPartyEmbeds(container) },
    { name: 'alm-features',    hasElement: '.alm-filters-container, .ajax-load-more-wrap', init: () => initializeAlmFeatures() },
    { name: 'alm-masonry',     container: true, hasElement: '.esr-masonry', init: ({ container }) => initAlmMasonry(container || document), destroy: ({ container }) => destroyAlmMasonry(container || document) },

    // --- Container-Scoped Theme Components (Feature-Flagged & Dynamic Imports) ---
    { name: 'faux-selects',    container: true, enabled: () => THEME_CONFIG.features.fauxSelects, hasElement: '.faux-select, select[data-faux-select]', init: async ({ container }) => {
        const { initializeFauxSelects } = await import('./ui/faux-select.js');
        initializeFauxSelects(container);
    } },
    { name: 'accordions',      container: true, enabled: () => THEME_CONFIG.features.accordions, hasElement: '.accordion, .accordion-group', init: async ({ container }) => {
        const { initializeAccordions } = await import('./ui/accordion.js');
        initializeAccordions(container);
    }, destroy: async () => {
        const { destroyAccordions } = await import('./ui/accordion.js');
        destroyAccordions();
    } },
    { name: 'esr-mapovis',     container: true, hasElement: '[data-mapovis-component]', init: async (ctx) => {
        const { initializeEsrMapovis } = await import('./integrations/esr-mapovis.js');
        initializeEsrMapovis(ctx);
    }, destroy: async (ctx) => {
        const { destroyEsrMapovis } = await import('./integrations/esr-mapovis.js');
        destroyEsrMapovis(ctx);
    } },
    { name: 'tabs',            container: true, enabled: () => THEME_CONFIG.features.tabs, hasElement: '.js-tabs', init: async ({ container }) => {
        const { initializeTabs } = await import('./ui/tabs.js');
        initializeTabs(container);
    }, destroy: async () => {
        const { destroyTabs } = await import('./ui/tabs.js');
        destroyTabs();
    } },
    { name: 'explore-map',     container: true, hasElement: '[data-explore-map]', init: async ({ container }) => {
        const { initializeExploreMaps } = await import('./ui/explore-map.js');
        initializeExploreMaps(container);
    }, destroy: async () => {
        const { destroyExploreMaps } = await import('./ui/explore-map.js');
        destroyExploreMaps();
    } },
    { name: 'project-filter',  container: true, hasElement: '[data-project-filter]', init: async ({ container }) => {
        const { initializeProjectFilters } = await import('./ui/project-filter.js');
        initializeProjectFilters(container);
    }, destroy: async () => {
        const { destroyProjectFilters } = await import('./ui/project-filter.js');
        destroyProjectFilters();
    } },
    { name: 'stacking-plan',   container: true, hasElement: '[data-stacking-plan]', init: async ({ container }) => {
        const { initializeStackingPlans } = await import('./ui/stacking-plan.js');
        initializeStackingPlans(container);
    }, destroy: async () => {
        const { destroyStackingPlans } = await import('./ui/stacking-plan.js');
        destroyStackingPlans();
    } },
    { name: 'info-bubbles',    container: true, enabled: () => THEME_CONFIG.features.infoBubbles, hasElement: '.info-bubble', init: async ({ container }) => {
        const { initializeInfoBubbles } = await import('./ui/info-bubble.js');
        initializeInfoBubbles(container);
    }, destroy: async () => {
        const { destroyInfoBubbles } = await import('./ui/info-bubble.js');
        destroyInfoBubbles();
    } },
    { name: 'read-reveal',     container: true, enabled: () => THEME_CONFIG.features.readReveal, hasElement: '.read-reveal', init: async ({ container }) => {
        const { initializeReadReveal } = await import('./ui/read-reveal.js');
        initializeReadReveal(container);
    } },
    { name: 'quick-search',    container: true, enabled: () => THEME_CONFIG.features.quickSearch, hasElement: '.quick-search', init: async ({ container }) => {
        const { initializeQuickSearch } = await import('./ui/quick-search.js');
        initializeQuickSearch(container);
    } },
    { name: 'faqs',            container: true, enabled: () => THEME_CONFIG.features.faqs, hasElement: '.faq-archive', init: async ({ container }) => {
        const { initializeFaqs } = await import('./features/faqs.js');
        initializeFaqs(container);
    }, destroy: async () => {
        const { destroyFaqs } = await import('./features/faqs.js');
        destroyFaqs();
    } },

    // --- Page-Level Theme Components (Feature-Flagged & Dynamic Imports) ---
    { name: 'video-players',   enabled: () => THEME_CONFIG.features.videoPlayers, hasElement: '.video-player, [data-video-display]', init: async ({ container }) => {
        const { initializeVideoPlayers } = await import('./ui/video/slideshow-loader.js');
        initializeVideoPlayers(container);
    }, destroy: async ({ container }) => {
        const { destroyVideoPlayers } = await import('./ui/video/slideshow-loader.js');
        destroyVideoPlayers(container);
    } },
    { name: 'properties-map',  enabled: () => THEME_CONFIG.features.propertiesMap, hasElement: '.properties-map, #properties-map', init: async ({ container }) => {
        const { initializePropertiesMap } = await import('./features/properties-map.js');
        initializePropertiesMap(container);
    }, destroy: async () => {
        const { destroyPropertiesMap } = await import('./features/properties-map.js');
        destroyPropertiesMap();
    } },
    { name: 'count-up',        enabled: () => THEME_CONFIG.features.countUp, hasElement: '.stats[data-animate="true"], .cards--19', init: async ({ container }) => {
        const { initializeCountUp } = await import('./features/count-up.js');
        initializeCountUp(container);
    }, destroy: async () => {
        const { destroyCountUp } = await import('./features/count-up.js');
        destroyCountUp();
    } },
    { name: 'hero-taglines',   enabled: () => THEME_CONFIG.features.heroTaglines, hasElement: '.hero__taglines', init: async ({ container }) => {
        const { initializeHeroTaglines } = await import('./features/hero-taglines.js');
        initializeHeroTaglines(container);
    }, destroy: async () => {
        const { destroyHeroTaglines } = await import('./features/hero-taglines.js');
        destroyHeroTaglines();
    } },
    { name: 'hero-scroll-opacity', enabled: () => THEME_CONFIG.features.heroScrollOpacity, hasElement: '.hero', init: async ({ container }) => {
        const { initializeHeroScrollOpacity } = await import('./features/hero-scroll-opacity.js');
        initializeHeroScrollOpacity(container);
    }, destroy: async () => {
        const { destroyHeroScrollOpacity } = await import('./features/hero-scroll-opacity.js');
        destroyHeroScrollOpacity();
    } },
    { name: 'scroll-to-top',   enabled: () => THEME_CONFIG.features.scrollToTop, hasElement: '#js-scroll-to-top', init: async ({ lenis }) => {
        const { initializeScrollToTop } = await import('./features/button-scroll-to-top.js');
        initializeScrollToTop(lenis);
    }, destroy: async ({ lenis }) => {
        const { destroyScrollToTop } = await import('./features/button-scroll-to-top.js');
        destroyScrollToTop(lenis);
    } },
    { name: 'slideshows',      enabled: () => THEME_CONFIG.features.slideshows, hasElement: '.swiper, .gallery.swiper, .layout__cards--6, .cards--2', init: async ({ debug }) => {
        const { slideshows } = await import('./ui/slideshow-manager.js');
        slideshows(window.pageSpecificData?.slideshows || [], debug);
    }, destroy: async ({ debug }) => {
        const { destroySlideshows } = await import('./ui/slideshow-manager.js');
        destroySlideshows(debug);
    } },
    { name: 'google-maps',     enabled: () => THEME_CONFIG.features.googleMaps, hasElement: '.google-map__canvas', init: async ({ container, debug }) => {
        const { initializeGoogleMaps } = await import('./integrations/google-maps-manager.js');
        initializeGoogleMaps(debug, container);
    }, destroy: async ({ container }) => {
        const { destroyGoogleMaps } = await import('./integrations/google-maps-manager.js');
        destroyGoogleMaps(container);
    } },
];

async function runInit(modules, ctx) {
    const pageName = ctx.data?.next?.namespace || (ctx.container ? 'container' : 'initial-load');
    logger.group(`Init Modules (${pageName})`, () => {
        for (const m of modules) {
            try {
                if (m.enabled && !m.enabled(ctx)) {
                    logger.logDisabled(m.name);
                    continue;
                }
                if (m.hasElement) {
                    const scope = m.container ? (ctx.container || document) : document;
                    if (!scope.querySelector(m.hasElement)) {
                        logger.logSkip(m.name, `No "${m.hasElement}" element`);
                        continue;
                    }
                }
                if (m.init) {
                    const t0 = performance.now();
                    const res = m.init(ctx);
                    if (res && typeof res.then === 'function') {
                        res.then(() => {
                            const dt = performance.now() - t0;
                            logger.logInit(m.name, ctx.container, dt);
                            activeModules.add(m.name);
                        }).catch(err => logger.logError(m.name, err));
                    } else {
                        const dt = performance.now() - t0;
                        logger.logInit(m.name, ctx.container, dt);
                        activeModules.add(m.name);
                    }
                }
            } catch (err) {
                logger.logError(m.name, err);
            }
        }
    });
}

async function runDestroy(modules, ctx) {
    const pageName = ctx.data?.current?.namespace || 'outgoing-page';
    logger.group(`Teardown Modules (${pageName})`, () => {
        for (let i = modules.length - 1; i >= 0; i--) {
            const m = modules[i];
            try {
                if (m.enabled && !m.enabled(ctx)) continue;
                if (m.destroy && activeModules.has(m.name)) {
                    const t0 = performance.now();
                    const res = m.destroy(ctx);
                    if (res && typeof res.then === 'function') {
                        res.then(() => {
                            const dt = performance.now() - t0;
                            logger.logDestroy(m.name, dt);
                            activeModules.delete(m.name);
                        }).catch(err => logger.logError(m.name, err));
                    } else {
                        const dt = performance.now() - t0;
                        logger.logDestroy(m.name, dt);
                        activeModules.delete(m.name);
                    }
                }
            } catch (err) {
                logger.logError(m.name, err);
            }
        }
    });
}

function initializeContainerScripts(container) {
    runInit(MODULES.filter(m => m.container), { container, lenis: lenisInstance, debug: THEME_CONFIG.debug, signal: pageAbortController?.signal });
}

function initializePageScripts(data) {
    if (!data || !data.next) return;
    if (pageAbortController) pageAbortController.abort();
    pageAbortController = new AbortController();
    runInit(MODULES, { container: data.next.container, lenis: lenisInstance, data, debug: THEME_CONFIG.debug, signal: pageAbortController.signal });
}

function cleanupPageScripts(data) {
    if (!data || !data.current) return;
    runDestroy(MODULES, { container: data.current.container, lenis: lenisInstance, data, debug: THEME_CONFIG.debug });
    if (pageAbortController) {
        pageAbortController.abort();
        pageAbortController = null;
    }
}

// =============================================================================
// APPLICATION BOOTSTRAP
// =============================================================================

document.addEventListener('DOMContentLoaded', () => {
    // 1. Initialize Smooth Scroll Engine (Lenis)
    lenisInstance = initializeScrollManager({ lerp: 0.2 });
    if (lenisInstance) {
        initializeLenisAnchorScrolling(lenisInstance);
    }

    // 2. Persistent Single-Run Services
    initializeDomLoadListeners();
    initializeMobileMenu();

    if (THEME_CONFIG.features.imageZoom && document.querySelector('.image-zoom, [data-zoom]')) {
        import('./ui/image-zoom.js').then(({ initializeImageZoom }) => initializeImageZoom(THEME_CONFIG.debug));
    }
    if (document.querySelector('[data-video-modal], .modal-video-trigger')) {
        import('./ui/video/modal-video-loader.js').then(({ initializeVideoPopupHandler }) => initializeVideoPopupHandler());
    }
    initializeVideoCoverResizerListeners('.video-container');
    if (document.querySelector('.read-reveal')) {
        import('./ui/read-reveal.js').then(({ initializeReadRevealListeners }) => initializeReadRevealListeners());
    }
    if (document.querySelector('.team-listing, #team-modal')) {
        import('./ui/team-modal.js').then(({ initializeTeamModal }) => initializeTeamModal());
    }

    // 3. Monitor Media Loading for Initial Page Hit
    if (!document.documentElement.classList.contains('page-ready')) {
        monitorMediaLoading(document, THEME_CONFIG.debug).then(() => {
            document.documentElement.classList.add('page-ready');
            document.body.classList.remove('is-loading');
        });
    }

    // 4. Initialize Barba.js Single Page Transitions
    initializeBarbaTransitions({
        debug: THEME_CONFIG.debug,
        timings: THEME_CONFIG.timings,
        onInitPageScripts: initializePageScripts,
        onCleanupPageScripts: cleanupPageScripts,
        getLenis: () => lenisInstance,
    });

    // 5. Load Web Fonts (Zero-touch: automatically awaits active template fonts)
    loadWebFonts([], [], THEME_CONFIG.debug);
});

// Clean teardown on full site unload
window.addEventListener('beforeunload', () => {
    destroyScrollManager();
});

// Expose public theme API globally
window.app = window.app || {};
window.app.initializeContainerScripts = initializeContainerScripts;

